import { useEffect, useRef } from "react";

/**
 * useWakeLock
 *
 * Mantém a tela do celular acesa enquanto `active` for true, usando a
 * Screen Wake Lock API nativa do navegador (Chrome/Samsung Internet no
 * Android, Safari iOS 16.4+). Sem suporte, vira no-op silencioso.
 *
 * O navegador libera o lock automaticamente quando a aba fica oculta
 * (troca de app, tela bloqueada no botão físico); ao voltar a ficar
 * visível, readquire sozinho se `active` ainda for true.
 *
 * Achado de campo (18/09/2026): relatado que o celular bloqueava mesmo com
 * treino em andamento. O spec do Wake Lock prevê um evento "release" quando
 * o browser libera o lock sozinho (troca de app, etc.) — mas WebKit em PWA
 * standalone no iOS tem histórico de simplesmente PARAR de segurar o lock
 * (a promise de request() resolve normalmente) sem nunca disparar esse
 * evento, deixando sentinelRef.current apontando pra um sentinel "morto" e
 * o listener de visibilitychange sem nada a fazer (achava que já tinha
 * lock). Heartbeat abaixo reforça o lock periodicamente enquanto visível,
 * independente de ter recebido "release" ou não — mesma categoria de
 * workaround usada por libs tipo NoSleep.js pra esse gap conhecido do
 * WebKit. Atenção: Low Power Mode ativo no iOS bloqueia a Wake Lock API
 * por completo (NotAllowedError) — isso este hook não tem como contornar,
 * é restrição do próprio SO.
 */
const REACQUIRE_HEARTBEAT_MS = 20000;

export function useWakeLock(active) {
  const sentinelRef = useRef(null);

  useEffect(() => {
    if (!active || typeof navigator === "undefined" || !("wakeLock" in navigator)) {
      return;
    }

    let cancelled = false;

    const request = async () => {
      try {
        const sentinel = await navigator.wakeLock.request("screen");
        if (cancelled) {
          sentinel.release().catch(() => {});
          return;
        }
        sentinelRef.current = sentinel;
        // O browser pode liberar o lock espontaneamente (gestão de energia)
        // sem gerar visibilitychange — re-adquirimos imediatamente se a página
        // ainda estiver visível e a sessão ainda estiver ativa.
        sentinel.addEventListener("release", () => {
          if (sentinelRef.current === sentinel) {
            sentinelRef.current = null;
            if (!cancelled && document.visibilityState === "visible") {
              request();
            }
          }
        });
      } catch (err) {
        // NotAllowedError (aba não visível, Low Power Mode no iOS), AbortError
        // etc. — silencioso
        console.warn("[useWakeLock] Falha ao solicitar wake lock:", err);
      }
    };

    request();

    const onVisibility = () => {
      if (document.visibilityState === "visible" && !sentinelRef.current) {
        request();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);

    // Heartbeat: reforça o lock a cada 20s enquanto a página estiver visível,
    // mesmo se sentinelRef.current já parecer preenchido — cobre o caso do
    // WebKit "esquecer" de segurar o lock sem avisar via "release" (ver nota
    // acima). request() é idempotente o bastante aqui: se o lock antigo
    // ainda estiver valendo, só ganha um sentinel novo redundante liberado
    // em seguida pelo próprio ciclo de "release" da versão anterior.
    const heartbeat = setInterval(() => {
      if (document.visibilityState === "visible") {
        request();
      }
    }, REACQUIRE_HEARTBEAT_MS);

    return () => {
      cancelled = true;
      clearInterval(heartbeat);
      document.removeEventListener("visibilitychange", onVisibility);
      if (sentinelRef.current) {
        sentinelRef.current.release().catch(() => {});
        sentinelRef.current = null;
      }
    };
  }, [active]);
}
